import { MKHttpRequest } from "../request";
import { MKHeaders } from "../../alias";
import { MKBitmovinPlayerError } from "../error";
import { MKEventBase, MKLog, MKSubtitleOverlay, MKUtils, MKPlatform, MKLocalStorage } from "../common";
import {
    MKAdQuartile, MKAdType, MKAssetType, MKBufferType, MKLogLevel, MKMediaType, MKMetadataType,
    MKPlayerEvent, MKPlayerType, MKStartOffsetTimelineReference, MKStreamType, MKTimeMode, MKViewMode, MKPlayerEngineType
} from "../../enums";
import {
    Player, DRMConfig, PlayerAPI, PlayerConfig, PlayerEvent, SourceConfig, LowLatencySyncConfig,
    BufferType, MediaType, LogLevel, MetadataType, TimeMode, ViewMode, ViewModeOptions, SubtitleTrack,
    PlaybackConfig, BufferConfig, AdaptationPlatformConfig, TimelineReferencePoint,
    HttpHeaders, AnalyticsConfig, ThumbnailTrack, HttpRequestType, HttpRequest,
    HttpResponse, TweaksConfig, DynamicAdaptationConfig, SubtitleCueEvent, SourceLabelingStreamTypeConfig,
    LiveConfigAtSourceLevel, StyleConfig,
} from "bitmovin-player";
import * as PlayStation4 from 'bitmovin-player/modules/bitmovinplayer-playstation4';
import * as PlayStation5 from 'bitmovin-player/modules/bitmovinplayer-playstation5';
import * as ServiceWorker from 'bitmovin-player/modules/bitmovinplayer-serviceworker-client';
import {
    UIFactory, UIManager
} from "bitmovin-player-ui";
import {
    MKPlayerConfig, MKSourceConfig, MKPlaybackEvent, MKPlayerEventBase,
    MKErrorEvent, MKAirplayChangedEvent, MKAspectRatioChangedEvent, MKAudioAdaptationEvent,
    MKAudioTrackEvent, MKAudioChangedEvent, MKAudioDownloadQualityChangeEvent, MKAudioDownloadQualityChangedEvent,
    MKAudioPlaybackQualityChangedEvent, MKAudioQualityEvent, MKAudioQualityChangedEvent, MKCastAvailableEvent,
    MKCastStartedEvent, MKCastWaitingForDeviceEvent, MKDurationChangedEvent, MKLowLatencySyncConfig,
    MKBufferLevel, MKPlayerCoreAPI, MKLatencyModeChangedEvent, MKMetadataEvent, MKMetadataChangedEvent, MKMetadataParsedEvent,
    MKUserInteractionEvent, MKPeriodSwitchedEvent, MKPlaybackSpeedChangedEvent, MKPlayerResizedEvent, MKSeekEvent,
    MKTargetLatencyChangedEvent, MKVideoAdaptationEvent, MKVideoDownloadQualityChangeEvent, MKVideoDownloadQualityChangedEvent,
    MKVideoPlaybackQualityChangedEvent, MKVideoQualityEvent, MKVideoQualityChangedEvent, MKVolumeChangedEvent, MKViewModeChangedEvent,
    MKViewModeOptions, MKTimeRange, MKVideoQuality, MKAudioQuality, MKDownloadedVideoData, MKDownloadedAudioData, MKAudioTrack,
    MKSubtitleTrack, MKSubtitleEvent, MKThumbnail, MKAdMarkerEvent, MKAdBreakStartedEvent, MKAdStartedEvent,
    MKAdFinishedEvent, MKAdQuartileEvent, MKAdBreakFinishedEvent, MKAdCompanionEvent, MKDownloadFinishedEvent, MKAdVerificationEvent,
    MKDrmLicenseAddedEvent, MKSegmentPlaybackEvent, MKSegmentRequestFinishedEvent, MKSourceOptions, MKCastConfig, MKClearKeyDRMServerConfig,
    MKDynamicAdaptationConfig, MKVSPPSessionIdEvent, MKBufferAPI, MKAdaptationAPI, MKDrmAPI, MKLowLatencyAPI, MKSegmentMap, MKSubtitlesAPI,
    MKSubtitleCueEvent, MKManifestAPI, MKAdClickThroughEvent,
    MKTimelineMarker,
    MKAnalyticsConfig,MKAd,MKAdBreak,MKAdBreakConfig,MKModuleInfo,
    MKAdvertisingConfig
} from "../../interfaces";
import { MKAdEvent, MKAdClickedEvent, MKAdManifestLoadedEvent, MKAdInteractionEvent, MKAdLinearityChangedEvent,MKAdBreakEvent } from "../../interfaces/events";
import { TimelineMarker } from "bitmovin-player-ui/dist/js/framework/uiconfig";
import { MKConstants } from "../../constants";

/**
 * Bitmovin player engine.
 * @internal
 */
export class MKBitmovinPlayer extends MKEventBase implements MKPlayerCoreAPI {
    // Bitmovin player handle
    private player: PlayerAPI;

    // Bitmovin player UIManager handle
    private playerUI?: UIManager;

    // Bitmovin player UI enabled?
    private playerUIEnabled: boolean;

    // Bitmovin player config
    private playerConfig: PlayerConfig;

    // HTML video element parent container
    private videoContainer: HTMLElement;

    // Custom subtitle overlay controller
    private subtitleOverlay?: MKSubtitleOverlay;

    // MKPlayer source config for the current source
    private sourceConfig?: MKSourceConfig;

    private isReload? : boolean;

    // Start playback at the given offset.
    //
    // Conditions:
    // - If stream contains preroll Ad, then preroll will be played before seeking to given offset.
    // - For Live
    //   - If value set is `0` then player will seek to beginning
    //   - If value set is `1` then player will seek to live edge
    //   - If value set is greater than `1` then seek to bookmark offset
    //   - Any other value if set the playback will start from beginning for live event and live edge for live.
    // - For VOD
    //   - Value set should be greater than `0` and less than `duration` of the VOD asset
    //   - Any other value if set then playback will start normally from start for VOD.
    private playStart?: number;

    // Fairplay Content Id
    private fpsContentId?: string;

    // HTTP Headers
    private headers: MKHeaders | null = null;

    // HTTP Headers for HLS_AES license requests only
    private headersForHlsAes: MKHeaders = {};

    // Flag to capture the first timeChanged update.
    private firstTimeChangedUpdate: boolean = false;

    // Indicates where the source contains preroll AD or not
    private hasPreroll: boolean = false;

    //Indicates whether the URL includes pre-roll ads as part of query param (ads=pr,mr || ads=mr,pr || ads=pr)
    private urlHasPrerollAdsParam: boolean = false;

    // Enable or Disable DAI AD Tracking beacons.
    private enableAdTrackingBeacons: boolean = true;

    private adMuteUrl = "";
    private adUnMuteUrl = "";
    private previousMuteState = false;
    private isAdPlaying = false;

    // Enable or Disable Subtitle overlay
    private enableSubtitleOverlay: boolean = true;

    // The current ad break event data when available
    private adBreakStartedEvent?: MKAdBreakStartedEvent;

    // The current ad started event data when available
    private adStartedEvent?: MKAdStartedEvent;

    // The current ad's quartile events start offsets.
    private adQuartileOffsets: AdQuartileOffset[] = [];

    // The current ad's tracking information list.
    private adTrackingList: AdTracking[] = [];

    // Flag to keep track of VSPP SessionId which is parsed from the manifest.
    private vsppSessionIdParsed = false;

    // local storage
    private localStorage: MKLocalStorage;

    // startOffset value of reload for live
    private reloadStartOffset = 0;

    // Flag to check if sourceLoaded/sourceUnloaded
    private isSourceLoaded: boolean;

    // Enable or disable Exclude Preroll Ad duration
    private excludePrerollFromVodDuration: boolean = false;

    // Preroll AD duration
    private preRollDuration = 0;

    //Skipping Quartile Events when tuning in the middle of an ad
    private skipQuartilesForCurrentAd = false;

    //If user is seeking to Preroll
    private seekingToPreroll: boolean= false;

    // Default bitmovin player buffer config
    private static readonly DEFAULT_BUFFER_CONFIG = {
        video: {
            forwardduration: 20,
            backwardduration: 12,
        },
        audio: {
            forwardduration: 20,
            backwardduration: 12,
        },
    };

    // Default bitmovin player playback config
    private static readonly DEFAULT_PLAYBACK_CONFIG = {
        muted: true,
        autoplay: true
    };

    // Class constructor
    constructor(videoContainer: HTMLElement, playerConfig: MKPlayerConfig) {
        super();

        if (!playerConfig.key) {
            throw new Error("Invalid player license key.");
        }

        // video container & element
        this.videoContainer = videoContainer;

        // Enable or disable AD tracking beacons.
        if (playerConfig.enableAdTrackingBeacons !== undefined) {
            this.enableAdTrackingBeacons = playerConfig.enableAdTrackingBeacons;
        }

        // Default player UI enabled?
        this.playerUIEnabled = playerConfig.ui ?? false;

        // Enable or disable subtitle overlay (default it is enabled)
        this.enableSubtitleOverlay = this.playerUIEnabled ? false : playerConfig.enableSubtitleOverlay ?? true;

        // Enable or disable Exclude Preroll Ad Duration
        if (playerConfig.excludePrerollFromVodDuration !== undefined) {
            this.excludePrerollFromVodDuration = playerConfig.excludePrerollFromVodDuration;
        }

        // Check local storage for client private key and return if available from local storage
        this.localStorage = MKLocalStorage.getInstance();

        this.isSourceLoaded = false;

        // Merge mandatory player tweaks with user specified tweaks
        playerConfig.tweaks = Object.assign({}, playerConfig.tweaks, {
            // These tweaks will overwite the user specified values
            app_id: playerConfig.appId,
            infinite_retries_for_live: playerConfig.tweaks?.infinite_retries_for_live ?? false,
            file_protocol: true,
            native_hls_parsing: playerConfig.tweaks?.native_hls_parsing ?? true,
            enable_seek_for_live: true,
            fairplay_ignore_duplicate_init_data_key_errors: true
        });

        // Merge mandatory player style with user specified style
        playerConfig.style = Object.assign({}, playerConfig.style, {
            width: playerConfig.style?.width ?? "100%",
        });

        // player configuration
        this.playerConfig = {
            key: playerConfig.key,
            ui: false, // keep this disabled because we are using bitmovin UIFactory and UIManager
            logs: {
                bitmovin: false,
                level: playerConfig.log?.level as unknown as LogLevel ?? LogLevel.ERROR
            },
            analytics: typeof (playerConfig.analytics) === "object" ? playerConfig.analytics as unknown as AnalyticsConfig : typeof (playerConfig.analytics) === "boolean" ? playerConfig.analytics : false,
            buffer: playerConfig.buffer as unknown as BufferConfig ?? MKBitmovinPlayer.DEFAULT_BUFFER_CONFIG,
            playback: playerConfig.playback as unknown as PlaybackConfig ?? MKBitmovinPlayer.DEFAULT_PLAYBACK_CONFIG,
            adaptation: playerConfig.adaptation as unknown as AdaptationPlatformConfig,
            remotecontrol: playerConfig.cast as unknown as MKCastConfig,
            tweaks: playerConfig.tweaks as unknown as TweaksConfig,
            style: playerConfig.style as unknown as StyleConfig,
            advertising: playerConfig.advertising as unknown as MKAdvertisingConfig,
            network: {
                preprocessHttpRequest: ((requestType: HttpRequestType, request: HttpRequest): Promise<HttpRequest> => {
                    if (request) {
                        if (requestType === HttpRequestType.DRM_CERTIFICATE_FAIRPLAY || requestType === HttpRequestType.DRM_LICENSE_FAIRPLAY) {
                            if (this.sourceConfig?.drm?.usingAzuki !== false && this.fpsContentId && this.sourceConfig?.drm?.fairplay) {
                                // update request URL to append key_id
                                request.url = MKUtils.updateURLQueryParams(request.url, { "key_id": this.fpsContentId });
                            }
                        }
                        if (Object.keys(this.headersForHlsAes).length > 0) {
                            // apply headers for KEY_HLS_AES requests.
                            if (requestType === HttpRequestType.KEY_HLS_AES) {
                                request.headers = this.headersForHlsAes;
                            }
                        }
                        if (this.headers && Object.keys(this.headers).length > 0) {
                            // apply headers for DASH manifest, HLS master and variant manifest requests.
                            if (requestType === HttpRequestType.MANIFEST_DASH || requestType === HttpRequestType.MANIFEST_HLS_MASTER || requestType === HttpRequestType.MANIFEST_HLS_VARIANT) {
                                request.headers = this.headers;
                            }
                        }
                    }
                    return Promise.resolve(request);
                }),
                preprocessHttpResponse: ((type, response): Promise<HttpResponse<any>> => {
                    // Parse VSPP Session ID from manifest response if not already done
                    if (!this.vsppSessionIdParsed && response.body &&
                        (type === HttpRequestType.MANIFEST_DASH || type === HttpRequestType.MANIFEST_HLS_MASTER)) {
                        this.vsppSessionIdParsed = true;
                        let body = response.body.toString();
                        const context = "context ";
                        let regex = new RegExp(context + "\.*\\b");
                        let match = body.match(regex);
                        if (!!match) {
                            let vsppSessionId = match[0].replace(context, "");
                            MKLog.debug(`preprocessHttpResponse - vsppSessionId: ` + vsppSessionId);
                            if (vsppSessionId.length) {
                                let event: MKVSPPSessionIdEvent = {
                                    vsppSessionId: vsppSessionId,
                                    type: MKPlayerEvent.VSPPSessionIdAvailable,
                                    timestamp: MKUtils.getCurrentTimestamp()
                                }
                                this.emit(MKPlayerEvent.VSPPSessionIdAvailable, event as unknown as MKVSPPSessionIdEvent);
                            }
                        }
                    }
                    // Intercepting the Widevine response(Decrption the response).
                    if (type === HttpRequestType.DRM_LICENSE_WIDEVINE && response.body instanceof ArrayBuffer) {
                        // Convert ArrayBuffer to Uint8Array
                        const unitBytes = new Uint8Array(response.body);
                        // Decode bytes to UTF-8 text
                        const utf8Text = new TextDecoder('utf-8').decode(unitBytes); 
                        const base64Response = /^[A-Za-z0-9+/=]+$/.test(utf8Text) && utf8Text.length % 4 === 0;
                        const azukiPublicKey = this.sourceConfig?.message_id as string; // azuki public key
                        if (base64Response && !!azukiPublicKey) { 
                            const clientPrivateKey = this.localStorage.getClientKeys()?.privateKey as string;
                            const sharedKey = MKUtils.deriveSharedSecretFromBase64(clientPrivateKey,azukiPublicKey);
                            const decryptedResponse = MKUtils.aesDecryption(utf8Text,sharedKey);
                            // update the response
                            (response as HttpResponse<ArrayBuffer>).body = MKUtils.base64ToArrayBuffer(decryptedResponse);
                        } else {
                            MKLog.debug('Widevine license response appears to be plain/binary (not encrypted)');
                        }
                    }
                    return Promise.resolve(response);
                }),
            },
            events: {
                [PlayerEvent.AdBreakFinished]: (event) => {
                    this.emit(MKPlayerEvent.AdBreakFinished, event as unknown as MKAdBreakEvent);
                },
                [PlayerEvent.AdBreakStarted]: (event) => {
                    const modifiedEvent = {
                        ...event, isClientSideAd : true
                    }
                    this.emit(MKPlayerEvent.AdBreakStarted, modifiedEvent as unknown as MKAdBreakEvent);
                },
                [PlayerEvent.AdClicked]: (event) => {
                    this.emit(MKPlayerEvent.AdClicked, event as unknown as MKAdClickedEvent);
                },
                [PlayerEvent.AdFinished]: (event) => {
                    this.emit(MKPlayerEvent.AdFinished, event as unknown as MKAdEvent);
                },
                [PlayerEvent.AdSkipped]: (event) => {
                    this.emit(MKPlayerEvent.AdSkipped, event as unknown as MKAdEvent);
                },
                [PlayerEvent.AdStarted]: (event) => {
                    const modifiedEvent = {
                        ...event, isClientSideAd : true
                    }
                    this.emit(MKPlayerEvent.AdStarted, modifiedEvent as unknown as MKAdEvent);
                },
                [PlayerEvent.AdLinearityChanged]: (event) => {
                    this.emit(MKPlayerEvent.AdLinearityChanged, event as unknown as MKAdLinearityChangedEvent);
                },
                [PlayerEvent.AdQuartile]: (event) => {
                    this.emit(MKPlayerEvent.AdQuartile, event as unknown as MKAdQuartileEvent);
                },
                [PlayerEvent.AdManifestLoaded]: (event) => {
                    this.emit(MKPlayerEvent.AdManifestLoaded, event as unknown as MKAdManifestLoadedEvent);
                },
                [PlayerEvent.AdError]: (event) => {
                    this.emit(MKPlayerEvent.AdError, event as unknown as MKErrorEvent);
                },
                [PlayerEvent.AdInteraction]: (event) => {
                    this.emit(MKPlayerEvent.AdInteraction, event as unknown as MKAdInteractionEvent);
                },
                [PlayerEvent.AirplayAvailable]: (event) => {
                    this.emit(MKPlayerEvent.AirplayAvailable, event as unknown as MKPlayerEventBase);
                },
                [PlayerEvent.AirplayChanged]: (event) => {
                    this.emit(MKPlayerEvent.AirplayChanged, event as unknown as MKAirplayChangedEvent);
                },
                [PlayerEvent.AspectRatioChanged]: (event) => {
                    this.emit(MKPlayerEvent.AspectRatioChanged, event as unknown as MKAspectRatioChangedEvent);
                },
                [PlayerEvent.AudioAdaptation]: (event) => {
                    this.emit(MKPlayerEvent.AudioAdaptation, event as unknown as MKAudioAdaptationEvent);
                },
                [PlayerEvent.AudioAdded]: (event) => {
                    this.emit(MKPlayerEvent.AudioAdded, event as unknown as MKAudioTrackEvent);
                },
                [PlayerEvent.AudioChanged]: (event) => {
                    this.emit(MKPlayerEvent.AudioChanged, event as unknown as MKAudioChangedEvent);
                },
                [PlayerEvent.AudioDownloadQualityChange]: (event) => {
                    this.emit(MKPlayerEvent.AudioDownloadQualityChange, event as unknown as MKAudioDownloadQualityChangeEvent);
                },
                [PlayerEvent.AudioDownloadQualityChanged]: (event) => {
                    this.emit(MKPlayerEvent.AudioDownloadQualityChanged, event as unknown as MKAudioDownloadQualityChangedEvent);
                },
                [PlayerEvent.AudioPlaybackQualityChanged]: (event) => {
                    this.emit(MKPlayerEvent.AudioPlaybackQualityChanged, event as unknown as MKAudioPlaybackQualityChangedEvent);
                },
                [PlayerEvent.AudioQualityAdded]: (event) => {
                    this.emit(MKPlayerEvent.AudioQualityAdded, event as unknown as MKAudioQualityEvent);
                },
                [PlayerEvent.AudioQualityChanged]: (event) => {
                    this.emit(MKPlayerEvent.AudioQualityChanged, event as unknown as MKAudioQualityChangedEvent);
                },
                [PlayerEvent.AudioQualityRemoved]: (event) => {
                    this.emit(MKPlayerEvent.AudioQualityRemoved, event as unknown as MKAudioQualityEvent);
                },
                [PlayerEvent.AudioRemoved]: (event) => {
                    this.emit(MKPlayerEvent.AudioRemoved, event as unknown as MKAudioTrackEvent);
                },
                [PlayerEvent.CastAvailable]: (event) => {
                    this.emit(MKPlayerEvent.CastAvailable, event as unknown as MKCastAvailableEvent);
                },
                [PlayerEvent.CastStart]: (event) => {
                    this.emit(MKPlayerEvent.CastStart, event as unknown as MKPlayerEventBase);
                },
                [PlayerEvent.CastStarted]: (event) => {
                    this.emit(MKPlayerEvent.CastStarted, event as unknown as MKCastStartedEvent);
                },
                [PlayerEvent.CastStopped]: (event) => {
                    this.emit(MKPlayerEvent.CastStopped, event as unknown as MKPlayerEventBase);
                },
                [PlayerEvent.CastWaitingForDevice]: (event) => {
                    this.emit(MKPlayerEvent.CastWaitingForDevice, event as unknown as MKCastWaitingForDeviceEvent);
                },
                [PlayerEvent.DVRWindowExceeded]: (event) => {
                    this.emit(MKPlayerEvent.DVRWindowExceeded, event as unknown as MKPlayerEventBase);
                },
                [PlayerEvent.Destroy]: (event) => {
                    this.emit(MKPlayerEvent.Destroy, event as unknown as MKPlayerEventBase);
                },
                [PlayerEvent.DownloadFinished]: (event) => {
                    this.emit(MKPlayerEvent.DownloadFinished, event as unknown as MKDownloadFinishedEvent);
                },
                [PlayerEvent.DrmLicenseAdded]: (event) => {
                    this.emit(MKPlayerEvent.DrmLicenseAdded, event as unknown as MKDrmLicenseAddedEvent);
                },
                [PlayerEvent.DurationChanged]: (event) => {
                    this.emit(MKPlayerEvent.DurationChanged, event as unknown as MKDurationChangedEvent);
                },
                [PlayerEvent.Error]: (event) => {
                    // MKPlayer errors have 4-7{submodule}-{code} format so we need
                    // to convert bitmovin error to MKPlayer error format and emit
                    const mkerror = new MKBitmovinPlayerError(event);
                    const mkerrorEvent: MKErrorEvent = {
                        timestamp: event.timestamp,
                        type: MKPlayerEvent.Error,
                        name: mkerror.name,
                        code: mkerror.code,
                        message: mkerror.message
                    };
                    this.emit(MKPlayerEvent.Error, mkerrorEvent);
                },
                [PlayerEvent.LatencyModeChanged]: (event) => {
                    this.emit(MKPlayerEvent.LatencyModeChanged, event as unknown as MKLatencyModeChangedEvent);
                },
                [PlayerEvent.Metadata]: (event) => {
                    const metadataEvent = event as unknown as MKMetadataEvent;

                    // emit the callback with the raw metadata
                    this.emit(MKPlayerEvent.Metadata, metadataEvent);

                    // process metadata
                    if (metadataEvent.metadataType === MKMetadataType.DATERANGE) {
                        // DATERANGE metadata object
                        const metadata: any = metadataEvent.metadata

                        // Check for DAI metadata
                        if ((metadata.class === "AD-BREAK" || metadata.class === "AD" || metadata.class === "ANALYTICS") &&
                            (metadata.clientAttributes && Object.keys(metadata.clientAttributes).length > 0)) {
                            if (metadata.class === "AD-BREAK") {
                                // initialize
                                this.adQuartileOffsets = [];
                                this.adStartedEvent = undefined;
                                this.adBreakStartedEvent = undefined;
                                if (metadata.clientAttributes.adType === "pre-roll") {
                                    this.preRollDuration = metadata.duration;
                                }

                                const eventType = metadata.id.endsWith('start') ? "playerEventAdBreakStart" : (metadata.id.endsWith('end') ? "playerEventAdBreakEnd" : "");
                                const currentTime = this.player.getCurrentTime();
                                const endTime = metadataEvent.start!! + metadata.duration;
                                const adStartTime = metadataEvent.start!! ?? 0;
                                const isMidAdTuneIn = metadata.clientAttributes.adType === "pre-roll" ? (currentTime - adStartTime > 1) && this.seekingToPreroll : currentTime > adStartTime;
                                if (eventType === "playerEventAdBreakStart" && currentTime >= metadataEvent.start!! && currentTime <= endTime) {
                                    if (isMidAdTuneIn) {
                                        this.skipQuartilesForCurrentAd = true;
                                        this.adQuartileOffsets = [];
                                    }
                                    // AdBreakStarted event data
                                    this.adBreakStartedEvent = {
                                        id: metadata.id.replace("-start", "") ?? "",
                                        adType: metadata.clientAttributes.adType === "pre-roll" ? MKAdType.Preroll : metadata.clientAttributes.adType === "mid-roll" ? MKAdType.Midroll : metadata.clientAttributes.adType === "post-roll" ? MKAdType.Postroll : MKAdType.Unknown,
                                        adsCount: metadata.clientAttributes.adsCount ?? metadata.clientAttributes.adIndex.split(":")[1] ?? 0,
                                        duration: metadata.duration ?? 0,
                                        offset: {
                                            start: metadataEvent.start ?? 0,
                                            end: metadataEvent.end ?? 0
                                        },
                                        timestamp: event.timestamp,
                                        type: MKPlayerEvent.AdBreakStarted
                                    }

                                    // emit AdBreakStarted event
                                    this.emit(MKPlayerEvent.AdBreakStarted, this.adBreakStartedEvent);
                                    this.isAdPlaying = true;
                                    if (this.getAnylticsSSAI()) {
                                        this.getAnylticsSSAI().adBreakStart({ adPosition: metadata.clientAttributes.adType });
                                    }

                                    this.notifyBeaconTracking(metadata.clientAttributes.adbreakStartUrls, "adBreakStart");
                                } else if (eventType === "playerEventAdBreakEnd") {
                                    this.notifyBeaconTracking(metadata.clientAttributes.adbreakEndUrls, "adBreakEnd");
                                }
                            }
                            if (this.adBreakStartedEvent) {
                                if (metadata.class === "AD") {
                                    // emit AdFinished event if the current AD is still active before emitting events for the next AD
                                    if (this.adStartedEvent) {
                                        const adFinishedEvent: MKAdFinishedEvent = {
                                            id: this.adStartedEvent.id,
                                            timestamp: MKUtils.getCurrentTimestamp(),
                                            type: MKPlayerEvent.AdFinished
                                        };
                                        this.emit(MKPlayerEvent.AdFinished, adFinishedEvent);
                                        if (this.getAnylticsSSAI()) {
                                            this.getAnylticsSSAI().adQuartileFinished('completed'); 
                                        } 
                                        this.adStartedEvent = undefined;
                                        this.adQuartileOffsets = [];
                                    }

                                    // AdStarted event data
                                    this.adStartedEvent = {
                                        id: metadata.clientAttributes.adId ?? metadata.id ?? "",
                                        adIndex: metadata.clientAttributes.adIndex.split(":")[0] ?? "",
                                        title: metadata.clientAttributes.adTitle,
                                        system: metadata.clientAttributes.adSystem,
                                        creativeId: metadata.clientAttributes.creativeId,
                                        creativeAdId: metadata.clientAttributes.creativeAdId,
                                        duration: metadata.duration ?? 0,
                                        offset: {
                                            start: metadataEvent.start ?? 0,
                                            end: metadataEvent.end ?? 0
                                        },
                                        timestamp: event.timestamp,
                                        type: MKPlayerEvent.AdStarted
                                    }
                                    if (this.getAnylticsSSAI()) {
                                        this.getAnylticsSSAI().adStart({ adId: metadata.clientAttributes.adId, adSystem: metadata.clientAttributes.adSystem });
                                    }

                                    // AdQuartile offsets
                                    if (!this.skipQuartilesForCurrentAd) {
                                        this.adQuartileOffsets = this.getAdQuartileOffsets(this.adStartedEvent.offset, this.adStartedEvent.duration);
                                    } else {
                                        this.adQuartileOffsets = [];
                                    }

                                    // emit the AdStarted event
                                    this.emit(MKPlayerEvent.AdStarted, this.adStartedEvent);
                                }
                                if (metadata.class === "ANALYTICS" && this.adStartedEvent) {
                                    // update adTrackingList for reporting ad tracking beacons
                                    if (this.enableAdTrackingBeacons) {
                                        this.updateAdTrackingList(metadata.clientAttributes.adUrls);
                                    }

                                    // ClickThrough Ad event
                                    if (!!metadata.clientAttributes.adClick) {
                                        const adClickThroughEvent: MKAdClickThroughEvent = {
                                            id: this.adStartedEvent.id,
                                            adClick: metadata.clientAttributes.adClick,
                                            adClickTracking: !!metadata.clientAttributes.adClickTracking ? metadata.clientAttributes.adClickTracking : undefined,
                                            timestamp: event.timestamp,
                                            type: MKPlayerEvent.AdClickThrough
                                        };
                                        this.emit(MKPlayerEvent.AdClickThrough, adClickThroughEvent);
                                    }
                                    
                                    if (!!metadata.clientAttributes.vpmute) {
                                        this.adMuteUrl = metadata.clientAttributes.vpmute
                                    }
                                    if (!!metadata.clientAttributes.vpunmute) {
                                        this.adUnMuteUrl = metadata.clientAttributes.vpunmute
                                    }

                                    // Ad verification event for OMSDK plugin
                                    if (!!metadata.clientAttributes.adVerVendor && !!metadata.clientAttributes.adVerJs && !!metadata.clientAttributes.adVerParams) {
                                        const adVerificationEventData: MKAdVerificationEvent = {
                                            timestamp: event.timestamp,
                                            type: MKPlayerEvent.AdVerification,
                                            scriptUrl: metadata.clientAttributes.adVerJS,
                                            params: metadata.clientAttributes.adVerParams,
                                            vendorKey: metadata.clientAttributes.adVerVendor
                                        };
                                        this.emit(MKPlayerEvent.AdVerification, adVerificationEventData);
                                    }

                                    // Companion Ads event
                                    if (!!metadata.clientAttributes.companionAds) {
                                        const companionAdEventData: MKAdCompanionEvent = {
                                            timestamp: event.timestamp,
                                            type: MKPlayerEvent.AdCompanion,
                                            id: this.adStartedEvent.id,
                                            data: metadata.clientAttributes.companionAds
                                        };
                                        this.emit(MKPlayerEvent.AdCompanion, companionAdEventData);
                                    }
                                }
                            }
                        }
                    }
                },
                [PlayerEvent.MetadataChanged]: (event) => {
                    this.emit(MKPlayerEvent.MetadataChanged, event as unknown as MKMetadataChangedEvent);
                },
                [PlayerEvent.MetadataParsed]: (event) => {
                    // metadata parsed event data
                    const metadataParsedEvent = event as unknown as MKMetadataParsedEvent;

                    // collect ad markers if available
                    if (metadataParsedEvent.metadataType === MKMetadataType.DATERANGE &&
                        "class" in metadataParsedEvent.metadata && metadataParsedEvent.metadata.class === "AD-BREAK" &&
                        "clientAttributes" in metadataParsedEvent.metadata && metadataParsedEvent.metadata.clientAttributes) {
                        const clientAttributes: any = metadataParsedEvent.metadata.clientAttributes;
                        if ("adType" in clientAttributes && "adsCount" in clientAttributes) {
                            // update presense of preroll in the current source
                            if (clientAttributes.adType === "pre-roll") {
                                this.hasPreroll = true;
                                this.preRollDuration = event.end ?? 0;
                            }

                            // prepare the ad marker event data
                            const adMarkerEvent: MKAdMarkerEvent = {
                                adMarker: {
                                    start: metadataParsedEvent.start ?? 0,
                                    end: metadataParsedEvent.end ?? 0
                                },
                                timestamp: MKUtils.getCurrentTimestamp(),
                                type: MKPlayerEvent.AdMarker
                            };

                            // emit the ad marker event
                            this.emit(MKPlayerEvent.AdMarker, adMarkerEvent);
                        }
                    }

                    // emit the metadata parsed event
                    this.emit(MKPlayerEvent.MetadataParsed, metadataParsedEvent);
                },
                [PlayerEvent.Muted]: (event) => {
                    this.emit(MKPlayerEvent.Muted, event as unknown as MKUserInteractionEvent);
                },
                [PlayerEvent.Paused]: (event) => {
                    this.emit(MKPlayerEvent.Paused, event as unknown as MKPlaybackEvent);
                },
                [PlayerEvent.PeriodSwitch]: (event) => {
                    this.emit(MKPlayerEvent.PeriodSwitch, event as unknown as MKPlayerEventBase);
                },
                [PlayerEvent.PeriodSwitched]: (event) => {
                    this.emit(MKPlayerEvent.PeriodSwitched, event as unknown as MKPeriodSwitchedEvent);
                },
                [PlayerEvent.Play]: (event) => {
                    this.emit(MKPlayerEvent.Play, event as unknown as MKPlaybackEvent);
                },
                [PlayerEvent.PlaybackFinished]: (event) => {
                    this.subtitleOverlay?.hide();
                    this.emit(MKPlayerEvent.PlaybackFinished, event as unknown as MKPlayerEventBase);
                },
                [PlayerEvent.PlaybackSpeedChanged]: (event) => {
                    this.emit(MKPlayerEvent.PlaybackSpeedChanged, event as unknown as MKPlaybackSpeedChangedEvent);
                },
                [PlayerEvent.PlayerResized]: (event) => {
                    this.subtitleOverlay?.updateFontSize();
                    this.emit(MKPlayerEvent.PlayerResized, event as unknown as MKPlayerResizedEvent);
                },
                [PlayerEvent.Playing]: (event) => {
                    this.emit(MKPlayerEvent.Playing, event as unknown as MKPlaybackEvent);
                },
                [PlayerEvent.Ready]: (event) => {
                    if(this.reloadStartOffset !== 0) {
                        this.seek(this.reloadStartOffset);
                        this.reloadStartOffset = 0;
                    }
                    this.emit(MKPlayerEvent.Ready, event as unknown as MKPlayerEventBase);
                },
                [PlayerEvent.Seek]: (event) => {
                    if(event.position < this.preRollDuration && this.preRollDuration !== 0 && !this.excludePrerollFromVodDuration) {
                        this.seekingToPreroll = true;
                    }
                    this.subtitleOverlay?.hide();
                    this.emit(MKPlayerEvent.Seek, event as unknown as MKSeekEvent);
                },
                [PlayerEvent.Seeked]: (event) => {
                    this.emit(MKPlayerEvent.Seeked, event as unknown as MKPlayerEventBase);
                    this.playStart = undefined;
                },
                [PlayerEvent.SegmentPlayback]: (event) => {
                    this.emit(MKPlayerEvent.SegmentPlayback, event as unknown as MKSegmentPlaybackEvent);
                },
                [PlayerEvent.SegmentRequestFinished]: (event) => {
                    this.emit(MKPlayerEvent.SegmentRequestFinished, event as unknown as MKSegmentRequestFinishedEvent);
                },
                [PlayerEvent.TimeShift]: (event) => {
                    this.subtitleOverlay?.hide();
                    this.emit(MKPlayerEvent.Seek, event as unknown as MKSeekEvent);
                },
                [PlayerEvent.TimeShifted]: (event) => {
                    this.emit(MKPlayerEvent.Seeked, event as unknown as MKPlayerEventBase);
                    this.playStart = undefined;
                },
                [PlayerEvent.ShowAirplayTargetPicker]: (event) => {
                    this.emit(MKPlayerEvent.ShowAirplayTargetPicker, event as unknown as MKPlayerEventBase);
                },
                [PlayerEvent.SourceLoaded]: (event) => {
                    this.isSourceLoaded = true;
                    // enable bitmovin player UI if enabled
                    if (this.playerUIEnabled) {
                        this.playerUI?.release();
                        this.playerUI = UIFactory.buildDefaultSmallScreenUI(this.player);
                    }

                    // setup custom subtitle overlay
                    const videoElement = this.getVideoElement();
                    if (this.enableSubtitleOverlay && !this.subtitleOverlay && videoElement) {
                        try {
                            this.subtitleOverlay = new MKSubtitleOverlay(videoElement);
                        } catch (error) {
                            MKLog.error(error);
                        }
                    }

                    // source loaded event data
                    const sourceLoadedEventData = event as unknown as MKPlayerEventBase;

                    // collect stream type and url for hls/dash/smooth and append this as custom data to source loaded event data
                    let streamUrl: string | undefined = "";
                    const streamType: MKStreamType = this.player.getStreamType() as unknown as MKStreamType;
                    const source = this.player.getSource();
                    if (source && (streamType === MKStreamType.Hls || streamType === MKStreamType.Dash || streamType === MKStreamType.Smooth)) {
                        streamUrl = source[streamType];
                    }
                    if (!!streamUrl) {
                        Object.assign(event, {
                            customData: {
                                streamType: streamType,
                                streamUrl: streamUrl
                            }
                        });
                    }

                    // emit source loaded event
                    this.emit(MKPlayerEvent.SourceLoaded, sourceLoadedEventData);
                },
                [PlayerEvent.SourceUnloaded]: (event) => {
                    this.isSourceLoaded = false;
                    if(this.isReload) {
                        this.isReload = false;
                        return;
                    }
                    this.emit(MKPlayerEvent.SourceUnloaded, event as unknown as MKPlayerEventBase);
                },
                [PlayerEvent.StallEnded]: (event) => {
                    this.emit(MKPlayerEvent.StallEnded, event as unknown as MKPlayerEventBase);
                },
                [PlayerEvent.StallStarted]: (event) => {
                    this.subtitleOverlay?.hide();
                    this.emit(MKPlayerEvent.StallStarted, event as unknown as MKPlayerEventBase);
                },
                [PlayerEvent.SubtitleAdded]: (event) => {
                    this.emit(MKPlayerEvent.SubtitleAdded, event as unknown as MKSubtitleEvent);
                },
                [PlayerEvent.SubtitleDisable]: (event) => {
                    this.emit(MKPlayerEvent.SubtitleDisable, event as unknown as MKSubtitleEvent);
                },
                [PlayerEvent.SubtitleDisabled]: (event) => {
                    this.subtitleOverlay?.disable();
                    this.emit(MKPlayerEvent.SubtitleDisabled, event as unknown as MKSubtitleEvent);
                },
                [PlayerEvent.SubtitleEnable]: (event) => {
                    this.emit(MKPlayerEvent.SubtitleEnable, event as unknown as MKSubtitleEvent);
                },
                [PlayerEvent.SubtitleEnabled]: (event) => {
                    this.subtitleOverlay?.enable();
                    this.emit(MKPlayerEvent.SubtitleEnabled, event as unknown as MKSubtitleEvent);
                },
                [PlayerEvent.CueEnter]: (event) => {
                    // emit CueEnter event
                    const subtitleCue = event as unknown as MKSubtitleCueEvent;
                    this.emit(MKPlayerEvent.CueEnter, subtitleCue);

                    // use the subtitle cue data for showing subtitle overlay
                    this.subtitleOverlay?.show(subtitleCue);
                },
                [PlayerEvent.CueExit]: (event) => {
                    // emit cue exit event
                    this.emit(MKPlayerEvent.CueExit, event as unknown as MKSubtitleCueEvent);

                    // hide subtitle overlay
                    this.subtitleOverlay?.hide();
                },
                [PlayerEvent.CueParsed]: (event) => {
                    // emit cue parsed event
                    this.emit(MKPlayerEvent.CueParsed, event as unknown as MKSubtitleCueEvent);
                },
                [PlayerEvent.CueUpdate]: (event) => {
                    // emit cue update event
                    this.emit(MKPlayerEvent.CueUpdate, event as unknown as MKSubtitleCueEvent);
                },
                [PlayerEvent.SubtitleRemoved]: (event) => {
                    this.subtitleOverlay?.hide();
                    this.emit(MKPlayerEvent.SubtitleRemoved, event as unknown as MKSubtitleEvent);
                },
                [PlayerEvent.TargetLatencyChanged]: (event) => {
                    this.emit(MKPlayerEvent.TargetLatencyChanged, event as unknown as MKTargetLatencyChangedEvent);
                },
                [PlayerEvent.TimeChanged]: (event) => {
                    // actions to perform on the very first time changed update
                    if (!this.firstTimeChangedUpdate) {
                        // reset the flag
                        this.firstTimeChangedUpdate = true;

                        // handle playStart
                        if (this.sourceConfig && this.urlHasPrerollAdsParam && !this.hasPreroll && this.playStart !== undefined) {
                            if (this.sourceConfig.assetType === MKAssetType.Live || this.sourceConfig.assetType === MKAssetType.Event) {
                                if (this.playStart === 1) {
                                    MKLog.debug("Will seek to live edge!");
                                    this.seekToLiveEdge();
                                } else {
                                    // will seek to either beginning or bookmark offset
                                    MKLog.debug(`Will seek to offset ${this.playStart}`);
                                    this.seek(this.playStart);
                                }
                            } else {
                                // For VOD, Catchup or DVR
                                if (this.sourceConfig.sourceOptions?.startOffsetTimelineReference === MKStartOffsetTimelineReference.End) {
                                    this.playStart = this.player.getDuration() - this.playStart;
                                }
                                MKLog.debug(`Will seek to offset ${this.playStart}`);
                                this.seek(this.playStart);
                            }
                        }
                    }
                    // notify mute/unmute adTracking beacons
                    if (this.isAdPlaying) {
                        this.checkAndPostAdMuteStatus();
                    }

                    // emit AD spcific events if applicable
                    this.emitAdEvents(event.time);

                    // to exculde the ad duration and pass event time position
                    if (this.sourceConfig?.assetType === MKAssetType.VOD && this.excludePrerollFromVodDuration && this.preRollDuration) {
                        event.time = event.time < this.preRollDuration ? event.time: event.time - this.preRollDuration;
                    }

                    // timed changed event data
                    const timeChangedEventdata = event as unknown as MKPlaybackEvent;

                    // emit time changed event
                    this.emit(MKPlayerEvent.TimeChanged, timeChangedEventdata);

                    // notify adTracking beacons if any applicable
                    if (this.enableAdTrackingBeacons) {
                        this.notifyAdTracking(timeChangedEventdata.time);
                    }
                },
                [PlayerEvent.Unmuted]: (event) => {
                    this.emit(MKPlayerEvent.Unmuted, event as unknown as MKUserInteractionEvent);
                },
                [PlayerEvent.VideoAdaptation]: (event) => {
                    this.emit(MKPlayerEvent.VideoAdaptation, event as unknown as MKVideoAdaptationEvent);
                },
                [PlayerEvent.VideoDownloadQualityChange]: (event) => {
                    this.emit(MKPlayerEvent.VideoDownloadQualityChange, event as unknown as MKVideoDownloadQualityChangeEvent);
                },
                [PlayerEvent.VideoDownloadQualityChanged]: (event) => {
                    this.emit(MKPlayerEvent.VideoDownloadQualityChanged, event as unknown as MKVideoDownloadQualityChangedEvent);
                },
                [PlayerEvent.VideoPlaybackQualityChanged]: (event) => {
                    this.emit(MKPlayerEvent.VideoPlaybackQualityChanged, event as unknown as MKVideoPlaybackQualityChangedEvent);
                },
                [PlayerEvent.VideoQualityAdded]: (event) => {
                    this.emit(MKPlayerEvent.VideoQualityAdded, event as unknown as MKVideoQualityEvent);
                },
                [PlayerEvent.VideoQualityChanged]: (event) => {
                    this.emit(MKPlayerEvent.VideoQualityChanged, event as unknown as MKVideoQualityChangedEvent);
                },
                [PlayerEvent.VideoQualityRemoved]: (event) => {
                    this.emit(MKPlayerEvent.VideoQualityRemoved, event as unknown as MKVideoQualityEvent);
                },
                [PlayerEvent.ViewModeChanged]: (event) => {
                    this.emit(MKPlayerEvent.ViewModeChanged, event as unknown as MKViewModeChangedEvent);
                },
                [PlayerEvent.VolumeChanged]: (event) => {
                    this.emit(MKPlayerEvent.VolumeChanged, event as unknown as MKVolumeChangedEvent);
                },
                [PlayerEvent.Warning]: (event) => {
                    if ("code" in event && event.code === 1401) {
                        // NETWORK_COULD_NOT_LOAD_MANIFEST - treat this as an error instead of warning!
                        const mkerror = new MKBitmovinPlayerError(event);
                        const mkerrorEvent: MKErrorEvent = {
                            timestamp: event.timestamp,
                            type: MKPlayerEvent.Error,
                            name: mkerror.name,
                            code: mkerror.code,
                            message: mkerror.message
                        };
                        this.emit(MKPlayerEvent.Error, mkerrorEvent);
                    } else {
                        // do not emit, just log the warning!
                        MKLog.warn(JSON.stringify(event, null, 4));
                    }
                }
            }
        };

        // additional player modules
        // some of these platform specific modules offer customizations and fixes along with optimizations for better playback experience.
        if (MKPlatform.isPlayStation4()) Player.addModule(PlayStation4.default);
        if (MKPlatform.isPlayStation5()) Player.addModule(PlayStation5.default);

        // Add ServiceWorker module in case of Safari browser
        if (MKPlatform.isIOSFamily()) {
            Player.addModule(ServiceWorker.default);

            this.playerConfig.location = {
                serviceworker: '/serviceWorker.js',
            };
        }

        // new player
        MKLog.debug(`Creating player with playerConfig: ${JSON.stringify(this.playerConfig, null, 4)}`);
        this.player = new Player(this.videoContainer, this.playerConfig);

        // Get rid of the bitmovin styling classes from the player container so that the app can control the styling when using their custom UI
        if (!this.playerUIEnabled) {
            const classNames = ['bitmovinplayer-container', 'aspect-16x9'];
            classNames.forEach(className => {
                this.videoContainer.classList.remove(className);
            });
            if (this.videoContainer.className === '') {
                this.videoContainer.removeAttribute('class');
            }
        }
    }

    readonly adaptation: MKAdaptationAPI = {
        setConfig: (adaptationConfig: MKDynamicAdaptationConfig): void => {
            this.player.adaptation.setConfig(adaptationConfig as unknown as DynamicAdaptationConfig);
        },

        getConfig: (): MKDynamicAdaptationConfig => {
            return this.player.adaptation.getConfig() as unknown as MKDynamicAdaptationConfig;
        }
    }

    readonly buffer: MKBufferAPI = {
        setTargetLevel: (type: MKBufferType, value: number, media: MKMediaType): void => {
            this.player.buffer.setTargetLevel(type as unknown as BufferType, value, media as unknown as MediaType);
        },

        getLevel: (type: MKBufferType, media: MKMediaType): MKBufferLevel => {
            const bufferLevel = this.player.buffer.getLevel(type as unknown as BufferType, media as unknown as MediaType);
            if (bufferLevel.level === null) bufferLevel.level = 0;
            if (bufferLevel.targetLevel === null) bufferLevel.targetLevel = 0;
            return bufferLevel as unknown as MKBufferLevel;
        }
    }

    readonly drm: MKDrmAPI = {
        isDRMSupported: (drmSystem: string): Promise<string> => {
            return this.player.isDRMSupported(drmSystem);
        },

        getSupportedDRM: (): Promise<string[]> => {
            return this.player.getSupportedDRM();
        },

        renewLicense: (drmLicenseId: string): Promise<void> => {
            return this.player.drm.renewLicense(drmLicenseId);
        }
    }

    readonly lowlatency: MKLowLatencyAPI = {
        setTargetLatency: (latency: number): void => {
            this.player.lowlatency.setTargetLatency(latency);
        },

        getTargetLatency: (): number => {
            // Bug 1684365: Value of Target Latency (TL) is 0 in Safari
            // Work-around: Get targetLatency from sourceConfig
            return this.player.getSource()?.live?.lowLatency?.targetLatency ?? 0;
        },

        setCatchupConfig: (config: MKLowLatencySyncConfig): void => {
            this.player.lowlatency.setCatchupConfig(config as unknown as LowLatencySyncConfig);
        },

        getCatchupConfig: (): MKLowLatencySyncConfig => {
            return this.player.lowlatency.getCatchupConfig() as unknown as MKLowLatencySyncConfig;
        },

        setFallbackConfig: (config: MKLowLatencySyncConfig): void => {
            this.player.lowlatency.setFallbackConfig(config as unknown as LowLatencySyncConfig);
        },

        getFallbackConfig: (): MKLowLatencySyncConfig => {
            return this.player.lowlatency.getFallbackConfig() as unknown as MKLowLatencySyncConfig;
        },

        getLatency: (): number => {
            return this.player.lowlatency.getLatency();
        }
    }

    readonly manifest: MKManifestAPI = {
        getManifest: (): string => {
            return this.player.getManifest() as unknown as string;
        }
    }

    readonly subtitles: MKSubtitlesAPI = {
        add: (subtitle: MKSubtitleTrack): void => {
            this.player.subtitles.add(subtitle as unknown as SubtitleTrack);
        },

        remove: (subtitleID: string): void => {
            this.player.subtitles.remove(subtitleID);
        },

        list: (): MKSubtitleTrack[] => {
            return this.player.subtitles.list() as unknown as MKSubtitleTrack[];
        },

        enable: (subtitleID: string, exclusive?: boolean): void => {
            this.player.subtitles.enable(subtitleID, exclusive);
        },

        disable: (subtitleID: string): void => {
            this.player.subtitles.disable(subtitleID);
        },

        cueEnter: (cue: MKSubtitleCueEvent): void => {
            this.player.subtitles.cueEnter(cue as unknown as SubtitleCueEvent);
        },

        cueExit: (cue: MKSubtitleCueEvent): void => {
            this.player.subtitles.cueExit(cue as unknown as SubtitleCueEvent);
        }
    }

    addCastMetadata(metadata: any): boolean {
        return this.player.addMetadata(MetadataType.CAST, metadata);
    }

    addTimelineMarker(timelineMarker: MKTimelineMarker): void {
        if (this.playerUIEnabled && this.playerUI) {
            this.playerUI.addTimelineMarker(timelineMarker as unknown as TimelineMarker);
        } else {
            MKLog.warn("PlayerUI is not enabled or available!");
        }
    }

    castStop(): void {
        this.player.castStop();
    }

    castVideo(): void {
        this.player.castVideo();
    }

    destroy(): Promise<void> {
        // reset subtitle overlay
        this.subtitleOverlay?.disable();
        this.subtitleOverlay?.destroy();
        this.subtitleOverlay = undefined;

        // release playerUI
        this.playerUI?.release();

        // destroy
        return this.player.destroy();
    }

    getAspectRatio(): number {
        return this.player.getAspectRatio();
    }

    getAudio(): MKAudioTrack | null {
        const audioTrack = this.player.getAudio();
        if (audioTrack) return audioTrack as unknown as MKAudioTrack;
        return null;
    }

    getAudioQuality(): MKAudioQuality {
        const audioQuality = this.player.getAudioQuality();
        return audioQuality as unknown as MKAudioQuality;
    }

    async scheduleAd(AdBreakConfig:MKAdBreakConfig) : Promise<MKAdBreak[]> {
        return this.player.ads.schedule(AdBreakConfig);
    }

    discardAdBreak(adBreakId:string):void {
        return this.player.ads.discardAdBreak(adBreakId);
    }

    getActiveAd():MKAd {
        return this.player.ads.getActiveAd() as unknown as MKAd;
    }

    getActiveAdBreak():MKAdBreak {
        return this.player.ads.getActiveAdBreak() as unknown as MKAdBreak;
    }

    getModuleInfo():MKModuleInfo {
        return this.player.ads.getModuleInfo();
    }

    isLinearAdActive():boolean {
        return this.player.ads.isLinearAdActive();
    }

    listAd():MKAdBreak[] {
        return this.player.ads.list()
    }

    async skipAd(): Promise<void>{
        return this.player.ads.skip();
    }

    getAvailableAudio(): MKAudioTrack[] {
        const audioTracks = this.player.getAvailableAudio();
        return audioTracks as unknown[] as MKAudioTrack[];
    }

    getAvailableAudioQualities(): MKAudioQuality[] {
        const audioQualities = this.player.getAvailableAudioQualities();
        return audioQualities as unknown[] as MKAudioQuality[];
    }

    getAvailableSegments(): MKSegmentMap {
        return this.player.getAvailableSegments() as unknown as MKSegmentMap;
    }

    getAvailableVideoQualities(): MKVideoQuality[] {
        const videoQualities = this.player.getAvailableVideoQualities();
        return videoQualities as unknown[] as MKVideoQuality[];
    }

    getBufferedRanges(): MKTimeRange[] {
        let bufferedRanges = this.player.getBufferedRanges();
        return bufferedRanges as unknown[] as MKTimeRange[];
    }

    getConfig(mergedConfig?: boolean): MKPlayerConfig {
        return this.player.getConfig(mergedConfig) as unknown as MKPlayerConfig;
    }

    getCurrentTime(mode?: MKTimeMode): number {
        const currentTime = this.player.getCurrentTime(mode as unknown as TimeMode);
        if(this.sourceConfig?.assetType === MKAssetType.VOD && this.excludePrerollFromVodDuration && this.preRollDuration && this.preRollDuration <= currentTime) {
            return currentTime - this.preRollDuration;
        }
        return currentTime;
    }

    getDownloadedAudioData(): MKDownloadedAudioData {
        const downloadedAudioData = this.player.getDownloadedAudioData();
        return downloadedAudioData as unknown as MKDownloadedAudioData;
    }

    getDownloadedVideoData(): MKDownloadedVideoData {
        const downloadedVideoData = this.player.getDownloadedVideoData();
        return downloadedVideoData as unknown as MKDownloadedVideoData;
    }

    getDroppedVideoFrames(): number {
        return this.player.getDroppedVideoFrames();
    }

    getDuration(): number {
        if (this.isLive()) {
            return (-1 * this.getMaxTimeShift());
        }
        if (this.excludePrerollFromVodDuration && this.preRollDuration && this.preRollDuration > 0 && this.sourceConfig?.assetType === MKAssetType.VOD) {
            return this.player.getDuration() - this.preRollDuration;
        }
        return this.player.getDuration();
    }

    getLiveLatency(): number {
        if (this.isLive()) {
            const wallClockTimeMsec = Date.now();
            const playerCurrentTimeMsec = this.getCurrentTime(MKTimeMode.AbsoluteTime) * 1000;
            return ((wallClockTimeMsec - playerCurrentTimeMsec) / 1000);
        }
        return 0;
    }

    getMaxTimeShift(): number {
        return this.player.getMaxTimeShift();
    }

    getPlaybackAudioData(): MKAudioQuality {
        const audioQuality = this.player.getPlaybackAudioData();
        return audioQuality as unknown as MKAudioQuality;
    }

    getPlaybackSpeed(): number {
        return this.player.getPlaybackSpeed();
    }

    getPlaybackVideoData(): MKVideoQuality {
        const videoQuality = this.player.getPlaybackVideoData();
        return videoQuality as unknown as MKVideoQuality;
    }

    getPlayerType(): MKPlayerType {
        const playerType = this.player.getPlayerType();
        return playerType as unknown as MKPlayerType;
    }

    getPlayerEngine(type: MKPlayerEngineType): any {
        if (type === MKPlayerEngineType.Bitmovin && this.player) {
            return this.player;
        }
        return null;
    }

    getSeekableRange(mode?: MKTimeMode): MKTimeRange {
        if (mode == MKTimeMode.RelativeTime && this.isLive()) {
            const end = this.getCurrentTime(mode) + (-1 * this.getTimeShift());      // current live edge offset
            const start = end - (-1 * this.getMaxTimeShift());                       // current start offset
            const seekableRange: MKTimeRange = {
                start: start < 0 ? 0 : start,
                end: end
            }
            return seekableRange;
        }
        const seekableRange = this.player.getSeekableRange();
        return seekableRange as unknown as MKTimeRange;
    }

    getSource(): MKSourceConfig | null {
        if (this.isSourceLoaded) {
            return this.player.getSource() as unknown as MKSourceConfig;
        }
        return null;
    }

    getStreamType(): MKStreamType {
        const streamType = this.player.getStreamType();
        return streamType as unknown as MKStreamType;
    }

    getThumbnail(time: number): MKThumbnail | null {
        time = time + this.player.getSeekableRange().start;
        const thumbnail = this.player.getThumbnail(time);
        if (thumbnail) return thumbnail as unknown as MKThumbnail;
        return null;
    }

    getTimeShift(): number {
        return this.player.getTimeShift();
    }

    getTimelineMarkers(): MKTimelineMarker[] {
        if (this.playerUIEnabled && this.playerUI) {
            return this.playerUI.getTimelineMarkers() as unknown as MKTimelineMarker[];
        }
        return [];
    }

    getTotalStalledTime(): number {
        return this.player.getTotalStalledTime();
    }

    getVersion(): string {
        // return bitmovin player version if called.
        return Player.version;
    }

    getVideoContainer(): HTMLElement {
        return this.player.getContainer();
    }

    getVideoElement(): HTMLVideoElement {
        return this.player.getVideoElement();
    }

    getVideoQuality(): MKVideoQuality {
        const videoQuality = this.player.getVideoQuality();
        let modifiedVideoQuality = {
            bitrate: videoQuality.bitrate === null ? 0 : videoQuality.bitrate,
            height: videoQuality.height === null ? 0 : videoQuality.height,
            width: videoQuality.width === null ? 0 : videoQuality.width
        };
        return modifiedVideoQuality as unknown as MKVideoQuality;
    }

    getViewMode(): MKViewMode {
        const viewMode = this.player.getViewMode();
        return viewMode as unknown as MKViewMode;
    }

    getVolume(): number {
        return this.player.getVolume();
    }

    hasEnded(): boolean {
        return this.player.hasEnded();
    }

    isAirplayActive(): boolean {
        return this.player.isAirplayActive();
    }

    isAirplayAvailable(): boolean {
        return this.player.isAirplayAvailable();
    }

    isCastAvailable(): boolean {
        return this.player.isCastAvailable();
    }

    isCasting(): boolean {
        return this.player.isCasting();
    }

    isLive(): boolean {
        return this.player.isLive();
    }

    isMuted(): boolean {
        return this.player.isMuted();
    }

    isPaused(): boolean {
        return this.player.isPaused();
    }

    isPlaying(): boolean {
        return this.player.isPlaying();
    }

    isStalled(): boolean {
        return this.player.isStalled();
    }

    isViewModeAvailable(viewMode: MKViewMode): boolean {
        return this.player.isViewModeAvailable(viewMode as unknown as ViewMode);
    }

    load(sourceConfig: MKSourceConfig, disableSeeking?: boolean): Promise<void> {
        // initialize and update variables and flags
        this.hasPreroll = false;
        this.urlHasPrerollAdsParam = false;
        this.adTrackingList = [];
        this.headersForHlsAes = {};
        this.playStart = undefined;
        this.fpsContentId = undefined;
        this.firstTimeChangedUpdate = false;
        this.sourceConfig = Object.assign({}, sourceConfig);
        this.vsppSessionIdParsed = false;
        let applyDefaultStartOffset: boolean = true;

        // construct the source config for bitmovin player
        const source: SourceConfig = {};
        source.title = sourceConfig.title;
        source.description = sourceConfig.description;
        source.poster = sourceConfig.poster;
        source.hls = sourceConfig.hls;
        source.dash = sourceConfig.dash;
        source.progressive = sourceConfig.progressive;
        source.smooth = sourceConfig.smooth;
        source.labeling = sourceConfig.labeling as unknown as SourceLabelingStreamTypeConfig;
        source.analytics = sourceConfig.analytics as unknown as AnalyticsConfig;
        source.subtitleTracks = sourceConfig.subtitleTracks as unknown as SubtitleTrack[];
        source.thumbnailTrack = sourceConfig.thumbnailTrack as unknown as ThumbnailTrack;
        source.live = sourceConfig.live as unknown as LiveConfigAtSourceLevel;

        const playbackUrl = source.hls || source.dash || "";
        this.urlHasPrerollAdsParam = this.checkUrlHasAdsParam(playbackUrl);

        if (sourceConfig.drm) {
            source.drm = Object.assign({}, sourceConfig.drm as unknown as DRMConfig);
            if (sourceConfig.drm.usingAzuki !== false && source.drm.fairplay) {
                // Implement DRM callbacks for Fairplay license and certificates as the
                // format of data retured by Azuki is not directly usable by the player.
                source.drm.fairplay.prepareContentId = ((url: string): string => {
                    const link = document.createElement('a');
                    link.href = url.substring(url.indexOf("skd://"));

                    // New backend changes included the key_id in query params,
                    // hence getting it from query params.
                    const params = new URL(link.href).searchParams;
                    var key_id = "";
                    params.forEach((value, key) => {
                        if (key === "key_id") {
                            key_id = value;
                        }
                    });

                    if (key_id !== "") {
                        this.fpsContentId = key_id;
                        return this.fpsContentId;
                    }

                    // If key_id is not in query params, using the legacy implementation to get key_id
                    if (link.href.length > 0) {
                        const urlParts = link.href.split("skd://", 2);
                        if (urlParts.length === 2) {
                            this.fpsContentId = urlParts[1];
                            return this.fpsContentId;
                        }
                    }
                    return link.hostname;
                });
                source.drm.fairplay.prepareCertificate = ((data: any): ArrayBuffer => {
                    const responseText = String.fromCharCode.apply(null, Array.from(new Uint8Array(data)));
                    const parser = new DOMParser();
                    const xmlDoc = parser.parseFromString(responseText, "text/xml");
                    const cert = xmlDoc.getElementsByTagName("cert")[0]?.childNodes[0]?.nodeValue;
                    if (cert === undefined || cert === null) {
                        return data;
                    }
                    const raw = MKUtils.base64Decode(cert ?? "");
                    const rawLength = raw.length;
                    let certificate = new Uint8Array(new ArrayBuffer(rawLength));
                    for (let i = 0; i < rawLength; i++) {
                        certificate[i] = raw.charCodeAt(i);
                    }
                    return certificate;
                });
                source.drm.fairplay.prepareMessage = ((event: any, _session: any): any => {
                    return MKUtils.base64EncodeUint8Array(event.message);
                });
                source.drm.fairplay.prepareLicense = ((data: any): string => {
                    const parser = new DOMParser();
                    const xmlDoc = parser.parseFromString(data, "text/xml");
                    const license = xmlDoc.getElementsByTagName("ckc")[0]?.childNodes[0]?.nodeValue;
                    return license ?? data;
                });
            }
            // Check for HLS_AES clearkey headers (MK custom implementation to support HLS_AES with headers)
            if (source.drm.clearkey && source.drm.clearkey.hasOwnProperty("LA_URL")) {
                const clearkeyConfig: MKClearKeyDRMServerConfig = Object.assign({}, source.drm.clearkey as unknown as MKClearKeyDRMServerConfig);
                if (clearkeyConfig.LA_URL.toLowerCase() === "hls_aes" && clearkeyConfig.headers && Object.keys(clearkeyConfig.headers).length > 0) {
                    this.headersForHlsAes = Object.assign({}, clearkeyConfig.headers);
                    source.drm = undefined; // clear the drm config as we don't need to pass this to bitmovin!
                }
            }
        }
        if (sourceConfig.sourceOptions !== undefined) {
            // update source options on the current source object
            source.options = {
                audioCodecPriority: sourceConfig.sourceOptions.audioCodecPriority,
                dashManifestWithCredentials: sourceConfig.sourceOptions.dashManifestWithCredentials,
                dashWithCredentials: sourceConfig.sourceOptions.dashWithCredentials,
                headers: sourceConfig.sourceOptions.headers as unknown as HttpHeaders,
                hlsManifestWithCredentials: sourceConfig.sourceOptions.hlsManifestWithCredentials,
                hlsWithCredentials: sourceConfig.sourceOptions.hlsWithCredentials,
                manifestWithCredentials: sourceConfig.sourceOptions.manifestWithCredentials,
                persistentPoster: sourceConfig.sourceOptions.persistentPoster,
                shouldRecreateSourceBuffersOnPeriodSwitch: sourceConfig.sourceOptions.shouldRecreateSourceBuffersOnPeriodSwitch,
                // startOffset and startOffsetTimelineReference we will set per requirement below...
                videoCodecPriority: sourceConfig.sourceOptions.videoCodecPriority,
                withCredentials: sourceConfig.sourceOptions.withCredentials
            }

            // Seek to the program load time to support restart of live program from specific time.
            if(sourceConfig.assetType === MKAssetType.Live && sourceConfig.sourceOptions.programLoadTime !== undefined ) {
                applyDefaultStartOffset = false; // must indicate not to apply defaults as we will configure it correctly here
                const programLoadTimeOffset = MKUtils.dateISOStringToTimestamp(sourceConfig.sourceOptions.programLoadTime);
                this.playStart = programLoadTimeOffset/1000;
                MKLog.debug(`Will seek to given program load time`);
            } else if (sourceConfig.sourceOptions.startOffset !== undefined) {  // handle startOffset if specifed for the current source
                applyDefaultStartOffset = false; // must indicate not to apply defaults as we will configure it correctly here

                if (!this.urlHasPrerollAdsParam) {
                    let startOffset = Math.abs(sourceConfig.sourceOptions.startOffset); // absolute value of `startOffset` is considered (converted to positive)
                    if (startOffset < MKConstants.EpochTimeCheck) {
                        let timelineReference = sourceConfig.sourceOptions.startOffsetTimelineReference as unknown as TimelineReferencePoint;
                        if (timelineReference === TimelineReferencePoint.End) {
                            startOffset = (startOffset === 0) ? -1 : -startOffset;
                        }
                        source.options = {
                            startOffset: startOffset,
                            startOffsetTimelineReference: timelineReference
                        };
                    } else {
                        // If startOffset is absolute/epoch time
                        this.reloadStartOffset = sourceConfig.sourceOptions.startOffset;
                    }
                } else {
                    this.urlHasPrerollAdsParam = true;
                    
                    if (sourceConfig.assetType === MKAssetType.Live || sourceConfig.assetType === MKAssetType.Event) {
                        if (sourceConfig.sourceOptions.startOffset < 0) {
                            // all negative offsets to be handled directly by the player. Playback will start directly
                            // at the specified offset and playback of preroll ad before seeking to offset is not enforced!
                            source.options.startOffset = sourceConfig.sourceOptions.startOffset;
                            source.options.startOffsetTimelineReference = sourceConfig.sourceOptions.startOffsetTimelineReference as unknown as TimelineReferencePoint;
                            MKLog.debug(`Will start playback directly at offset ${sourceConfig.sourceOptions.startOffset}`);
                        } else if (sourceConfig.sourceOptions.startOffset === 0) {
                            if (sourceConfig.sourceOptions.startOffsetTimelineReference === MKStartOffsetTimelineReference.Start) {
                                source.options.startOffset = -1;
                                source.options.startOffsetTimelineReference = TimelineReferencePoint.Start;
                                MKLog.debug(`Will start playback from the beginning!`);
                            } else if (sourceConfig.sourceOptions.startOffsetTimelineReference === MKStartOffsetTimelineReference.End) {
                                this.playStart = 1;
                                MKLog.debug(`Will play preroll if available and then seek to live edge!`);
                            } else {
                                // default for live and events when no startOffsetTimelineReference is specified
                                if (sourceConfig.assetType === MKAssetType.Live) {
                                    this.playStart = 1;
                                    MKLog.debug(`Will play preroll if available and then seek to live edge!`);
                                } else {
                                    source.options.startOffset = -1;
                                    source.options.startOffsetTimelineReference = TimelineReferencePoint.Start;
                                    MKLog.debug(`Will start playback from the beginning!`);
                                }
                            }
                        } else {
                            // for both live and live events, irrespective of what the default start point is the player will seek to the
                            // given offset after playing preroll ad if present in the stream, else will seek to offset as soon as playback starts
                            this.playStart = sourceConfig.sourceOptions.startOffset;
                            MKLog.debug(`Will play preroll if available else seek to offset ${this.playStart} as soon as playback starts!`);
                        }
                    } else {
                        // For VOD, Catchup and DVR (not in-progress DVR)
                        if (sourceConfig.sourceOptions.startOffset > 0) {
                            if (sourceConfig.registered !== undefined) {
                                // will play preroll if available and then seek to given offset for registered source
                                this.playStart = sourceConfig.sourceOptions.startOffset;
                                MKLog.debug(`Will play preroll if available else seek to offset ${this.playStart} as soon as playback starts!`);
                            } else {
                                // will seek to given offset directly for direct source
                                source.options.startOffset = sourceConfig.sourceOptions.startOffset
                                MKLog.debug(`Will start playback directly at offset ${sourceConfig.sourceOptions.startOffset}`);
                            }
                        } else {
                            MKLog.warn(`Unsupported startOffset: ${sourceConfig.sourceOptions.startOffset} specified in sourceOptions!`);
                        }
                    }
                }
            }
        }

        // Bug 1624470 : Set SourceOptions to start Low Latency streams from LiveEdge
        if (sourceConfig.enableLowLatency) {
            if (sourceConfig.assetType === MKAssetType.Live || sourceConfig.assetType === MKAssetType.Event || sourceConfig.registered === undefined) {
                source.options = {
                        startOffset: -1,
                        startOffsetTimelineReference: TimelineReferencePoint.End
                    }
                applyDefaultStartOffset = false;
            }
        }

        if (applyDefaultStartOffset) {
            applyDefaultStartOffset = false; // reset the flag
            // for in-progress DVR playback we need to force the player to start playback from the beginning
            // as for this type of DVR the source manifest is dynamic and growing as playback happens, hence
            // player treats in-progress DVR as `live` due to which playback will start at the live edge.
            if (sourceConfig.assetType === MKAssetType.DVR) {
                source.options = {
                    startOffset: 1,
                    startOffsetTimelineReference: TimelineReferencePoint.Start
                }
            }
            if (sourceConfig.assetType === MKAssetType.Live) {
                this.playStart = 1
                MKLog.debug(`Will play preroll if available and then seek to live edge!`);
            }
        }

        // Finally append any timeline markers if available
        if (sourceConfig.timelineMarkers) {
            // using try catch as `markers` is not defined in the documentation
            // but still this works, so being safe about its usage here...
            try {
                Object.assign(source, {
                    markers: sourceConfig.timelineMarkers as unknown as TimelineMarker[]
                });
            } catch {
                MKLog.warn("Failed to add given timeline markers for this source, please try using addTimelineMarker API instead!");
            }
        }

        // Override to set widevine drm robustness
        if (!!MKPlatform.wvDrmRobustnessLevel && source.drm?.widevine) {
            source.drm.widevine.audioRobustness = "HW_SECURE_CRYPTO";
            source.drm.widevine.videoRobustness = "HW_SECURE_CRYPTO";
        }

        // load the source on the player
        MKLog.debug(`Starting playback with source: ${JSON.stringify(source, null, 4)}`);
        return this.player.load(source, undefined, disableSeeking);
    }

    mute(issuer?: string): void {
        this.player.mute(issuer);
    }

    pause(issuer?: string): void {
        this.player.pause(issuer);
    }

    play(issuer?: string): Promise<void> {
        return this.player.play(issuer);
    }

    preload(): void {
        this.player.preload();
    }

    reload(start?: string, sourceOptions?: MKSourceOptions): Promise<void> {
        this.isReload = true;
        if (!this.sourceConfig) {
            return Promise.reject(new Error("Invalid source configuration!"));
        }

        //If the startOffset value is absolute epoch time
        if(sourceOptions?.startOffset && sourceOptions.startOffset > MKConstants.EpochTimeCheck) {
            this.reloadStartOffset = sourceOptions?.startOffset;
        }

        const sourceConfig = Object.assign({}, this.sourceConfig);
        sourceConfig.sourceOptions = Object.assign({}, sourceOptions);

        if (start !== undefined) {
            if (!this.player.isLive()) {
                return Promise.reject(new Error("Not a live source, start time can only be specified for live!"));
            }
            if (!MKUtils.isValidISODateString(start)) {
                return Promise.reject(new Error("Invalid start date-time specified!"));
            }
            if (!!sourceConfig.hls) {
                // update HLS source URL with `start=<specifiedStartTime>&end=END`
                sourceConfig.hls = MKUtils.updateURLQueryParams(sourceConfig.hls, { "start": start.toLocaleUpperCase(), "end": "END" });
            }
            if (!!sourceConfig.dash) {
                // update DASH source URL with `start=<specifiedStartTime>&end=END`
                sourceConfig.dash = MKUtils.updateURLQueryParams(sourceConfig.dash, { "start": start.toLocaleUpperCase(), "end": "END" });
            }
        }

        // reload
        return this.load(sourceConfig);
    }

    removeTimelineMarker(timelineMarker: MKTimelineMarker): boolean {
        if (this.playerUIEnabled && this.playerUI) {
            return this.playerUI.removeTimelineMarker(timelineMarker as unknown as TimelineMarker);
        }
        MKLog.warn("PlayerUI is not enabled or available!");
        return false;
    }

    seek(time: number, issuer?: string): boolean {
        if (this.isLive()) {
            // use the given time to get the correct seekable range (either relative or absolute)
            let seekableRange = this.getSeekableRange(time < MKConstants.EpochTimeCheck ? MKTimeMode.RelativeTime : MKTimeMode.AbsoluteTime);
            
            // convert given time to negative relative seek offset
            let seekOffset = time - seekableRange.end;

            // ensure that seek offset is within range [maxTimeShift..0] else seek to live edge as correction
            seekOffset = seekOffset < this.getMaxTimeShift() ? this.getMaxTimeShift() : seekOffset > 0 ? 0 : seekOffset;

            // seek
            this.player.timeShift(seekOffset, issuer);
            return true;
        } else {
            if (this.excludePrerollFromVodDuration && this.preRollDuration && this.preRollDuration <= this.player.getCurrentTime(TimeMode.RelativeTime)) {
                return this.player.seek(time + this.preRollDuration, issuer);
            }
            return this.player.seek(time, issuer);
        }
    }

    seekToLiveEdge(issuer?: string): void {
        if (this.isLive()) {
            this.player.timeShift(0, issuer);
        }
    }

    setAspectRatio(aspectratio: string | number): void {
        this.player.setAspectRatio(aspectratio);
    }

    setAudio(trackID: string): void {
        this.player.setAudio(trackID);
    }

    setAudioQuality(audioQualityID: string): void {
        this.player.setAudioQuality(audioQualityID);
    }

    setHttpHeaders(headers: MKHeaders | null): void {
        this.headers = headers;
    }

    setLogLevel(level: MKLogLevel): void {
        this.player.setLogLevel(level as unknown as LogLevel);
    }

    setPlaybackSpeed(speed: number): void {
        this.player.setPlaybackSpeed(speed);
    }

    setPosterImage(url: string, keepPersistent: boolean): void {
        this.player.setPosterImage(url, keepPersistent);
    }

    setVideoElement(videoElement: HTMLVideoElement): void {
        this.player.setVideoElement(videoElement);
    }

    setVideoQuality(videoQualityID: string): void {
        this.player.setVideoQuality(videoQualityID);
    }

    setViewMode(viewMode: MKViewMode, options?: MKViewModeOptions): void {
        const viewModeOptions: ViewModeOptions = {};
        if (options) viewModeOptions as unknown as ViewModeOptions;
        this.player.setViewMode(viewMode as unknown as ViewMode, viewModeOptions);
    }

    setVolume(volume: number, issuer?: string): void {
        this.player.setVolume(volume, issuer);
    }

    setAnalyticsCustomData(customData: MKAnalyticsConfig): void {
        (this.player as any).analytics.setCustomData(customData);
    }

    setSubtitleStyle(styleOptions: any): void {
        this.subtitleOverlay?.setSubtitleUI(styleOptions);
    }

    showAirplayTargetPicker(): void {
        this.player.showAirplayTargetPicker();
    }

    unload(): Promise<void> {
        // reset subtitle overlay
        this.subtitleOverlay?.disable();
        this.subtitleOverlay?.destroy();
        this.subtitleOverlay = undefined;

        // release UI
        this.playerUI?.release();

        // TODO: FIXME: with every load-unload we keep adding a div with class
        // bitmovinplayer-poster. This is happening only with this SDK code and
        // not with sample code using bitmovin player directly. So until we find
        // the root cause, manually removing these nodes from here...
        const posters = document.querySelectorAll('.bitmovinplayer-poster');
        posters.forEach(poster => {
            poster.remove();
        });

        // unload
        return this.player.unload();
    }

    unmute(issuer?: string): void {
        this.player.unmute(issuer);
    }

    /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // Class Private Methods
    /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    /**
     * Get AD Quartile event start offsets.
     *
     * @param adOffset current AD's time range (start and end offsets)
     * @param adDuration current AD's duration
     * @returns an array containing start time offset for different AD quartiles.
     */
    private getAdQuartileOffsets(adOffset: MKTimeRange, adDuration: number): AdQuartileOffset[] {
        let quartileOffsets: AdQuartileOffset[] = [];
        if (adOffset.start >= 0 && adOffset.end > 0 && adDuration > 0) {
            quartileOffsets.push({
                quartile: MKAdQuartile.FirstQuartile,
                startOffset: adOffset.start + (adDuration / 4)
            });
            quartileOffsets.push({
                quartile: MKAdQuartile.Midpoint,
                startOffset: adOffset.start + (adDuration / 2)
            });
            quartileOffsets.push({
                quartile: MKAdQuartile.ThirdQuartile,
                startOffset: adOffset.end - (adDuration / 4)
            });
        }
        return quartileOffsets;
    }

    private getAnylticsSSAI(): any {
        let analyticsSSAI: any;
        if ((this.player as any).analytics && (this.player as any).analytics.ssai) {
            analyticsSSAI = (this.player as any).analytics.ssai;
        }
        return analyticsSSAI;
    }
    /**
     * Emit any applicable AD events for the current player position.
     *
     * @param currentTime the current player position.
     */
    private emitAdEvents(currentTime: number) {
        if (this.adBreakStartedEvent && this.adStartedEvent) {
            if (!this.skipQuartilesForCurrentAd) {
            // emit AdQuartile events for the current AD
                let adQuartileOffsetsLength = this.adQuartileOffsets.length;
                while (adQuartileOffsetsLength--) {
                    if (currentTime >= this.adQuartileOffsets[adQuartileOffsetsLength].startOffset) {
                        const adQuartileEvent: MKAdQuartileEvent = {
                            id: this.adStartedEvent?.id ?? "",
                            quartile: this.adQuartileOffsets[adQuartileOffsetsLength].quartile,
                            timestamp: MKUtils.getCurrentTimestamp(),
                            type: MKPlayerEvent.AdQuartile
                        };
                        this.emit(MKPlayerEvent.AdQuartile, adQuartileEvent);
                        this.adQuartileOffsets.splice(adQuartileOffsetsLength, 1);

                        if (this.getAnylticsSSAI()) {
                            // SSAI Ad Quartile Tracking Analytic
                            //The different quartiles are FirstQuartile | Midpoint | ThirdQuartile
                            switch (adQuartileEvent.quartile) {
                                case MKAdQuartile.FirstQuartile:
                                    this.getAnylticsSSAI().adQuartileFinished('first');
                                    break;
                                case MKAdQuartile.Midpoint:
                                    this.getAnylticsSSAI().adQuartileFinished('midpoint');
                                    break;
                                case MKAdQuartile.ThirdQuartile:
                                    this.getAnylticsSSAI().adQuartileFinished('third');
                                    break;
                            }
                        }
                    }
                }
            } else {
                MKLog.debug("Skipping quartile events due to mid-ad tune-in");
            }

            // emit AdBreakFinished event for the current AD break
            if (currentTime >= this.adBreakStartedEvent.offset.end) {
                // emit AdFinished event if the current AD is active
                if (this.adStartedEvent) {
                    const adFinishedEvent: MKAdFinishedEvent = {
                        id: this.adStartedEvent.id,
                        timestamp: MKUtils.getCurrentTimestamp(),
                        type: MKPlayerEvent.AdFinished
                    };
                    this.emit(MKPlayerEvent.AdFinished, adFinishedEvent);
                    this.adStartedEvent = undefined;
                    this.adQuartileOffsets = [];
                }

                // emit AdBreakFinished event
                const adType = this.adBreakStartedEvent.adType;
                const adBreakFinishedEvent: MKAdBreakFinishedEvent = {
                    id: this.adBreakStartedEvent.id,
                    timestamp: MKUtils.getCurrentTimestamp(),
                    type: MKPlayerEvent.AdBreakFinished
                }
                this.emit(MKPlayerEvent.AdBreakFinished, adBreakFinishedEvent);
                this.isAdPlaying = false;
                this.skipQuartilesForCurrentAd = false;
                this.adQuartileOffsets = [];
                if (this.getAnylticsSSAI()) {
                    this.getAnylticsSSAI().adBreakEnd();
                }
                this.adBreakStartedEvent = undefined;

                // handle playStart
                if (adType === MKAdType.Preroll && this.urlHasPrerollAdsParam && this.sourceConfig && this.playStart !== undefined) {
                    if (this.sourceConfig.assetType === MKAssetType.Live || this.sourceConfig.assetType === MKAssetType.Event) {
                        if (this.playStart === 1) {
                            MKLog.debug("Will seek to live edge as preroll ended!");
                            this.seekToLiveEdge();
                        } else {
                            // will seek to given bookmark offset
                            if (this.playStart < MKConstants.EpochTimeCheck) {
                                // if playStart is relative time and timelineReferencePoint is END then, convert the playStart value to seek position from the liveEdge.
                                if (this.sourceConfig.sourceOptions?.startOffsetTimelineReference === MKStartOffsetTimelineReference.End) {
                                    let seekableRange = this.getSeekableRange(MKTimeMode.RelativeTime);
                                    this.playStart = seekableRange.end - this.playStart;
                                }
                            }
                            MKLog.debug(`Will seek to given offset ${this.playStart} as preroll ended!`);
                            this.seek(this.playStart);
                        }
                    } else { // For VOD, Catchup or DVR
                        if (this.sourceConfig.sourceOptions?.startOffsetTimelineReference === MKStartOffsetTimelineReference.End) {
                            this.playStart = this.player.getDuration() - this.playStart;
                        }
                        MKLog.debug(`Will seek to given offset ${this.playStart} as preroll ended!`);
                        this.seek(this.playStart);
                    }
                }
            }
        }
    }

    /**
     * Update the AdTracking list.
     *
     * @param adUrls the string containing AdTracking URL's.
     */
    private updateAdTrackingList(adUrls: string) {
        if (!!adUrls) {
            const urlParts = adUrls.split(" ");
            urlParts.forEach((urlPart) => {
                const trackingInfoParts = urlPart.split("|");
                if (trackingInfoParts.length > 1 && this.adStartedEvent) {
                    let offset = parseFloat(trackingInfoParts[0]);
                    if (isNaN(offset)) offset = 0;
                    const time = this.adStartedEvent.offset.start + offset;
                    const urls: string[] = [];
                    for (let index = 1; index < trackingInfoParts.length; index++) {
                        urls.push(trackingInfoParts[index]);
                    }
                    if (urls.length > 0) {
                        // update adTrackingList
                        this.adTrackingList.push({
                            time: time,
                            urls: urls
                        });
                    }
                }
            });
        }
    }

    /**
     * Notify AD tracking beacons applicable for the current time offset.
     *
     * @param currentTime the player current time offset.
     */
    private notifyAdTracking(currentTime: number) {
        let index = this.adTrackingList.length;
        while (index--) {
            if (currentTime >= this.adTrackingList[index].time && this.adTrackingList[index].urls.length > 0) {
                this.adTrackingList[index].urls.forEach((url) => {
                    const request = new MKHttpRequest();
                    request.get(url)
                        .then(() => {
                            MKLog.debug(`Ad tracking beacon notified successfully for url: ${url}`);
                        })
                        .catch(() => {
                            MKLog.warn(`Ad tracking beacon failed for url: ${url}`);
                        });
                });
                this.adTrackingList.splice(index, 1);
            }
        }
    }

    private checkAndPostAdMuteStatus() {
        const isMuted = this.player.isMuted();
    
        // Check if the mute state has changed
        if (isMuted !== this.previousMuteState) {
            // Update the previous mute state
            this.previousMuteState = isMuted;
    
            // Determine which URL to use based on the current mute state
            if (isMuted && this.adMuteUrl) {
                this.notifyBeaconTracking(this.adMuteUrl, "mute");
            } else if (!isMuted && this.adUnMuteUrl) {
                this.notifyBeaconTracking(this.adUnMuteUrl, "unmute");
            }
        }
    }
    
    private notifyBeaconTracking(url?: string, eventType?: string) {
        if (!url) {
            return;
        }

        const request = new MKHttpRequest();
        const trackingURLs = url.indexOf("|") > 0 ? url.split("|") : [url];
        trackingURLs.forEach((url) => {
            request.get(url)
                .then(() => {
                    MKLog.debug(`${eventType} tracking beacon notified for URL: ${url}`);
                })
                .catch(() => {
                    MKLog.warn(`${eventType} tracking beacon failed for URL: ${url}`);
                });
        });
    }

    /**
     * Checks if passed `url` contains `pr` value in `ads` query parameter
     * 
     * @param url the url to check for presence of preroll ad
     * @returns `true` if `ads` query parameter contains `pr` value, `false` otherwise
     */
    private checkUrlHasAdsParam(url: string): boolean {
        try {
            const parsedUrl = new URL(url);
            const adsParam = parsedUrl.searchParams.get("ads");
            return adsParam?.split(",").includes("pr") ?? false;
        } catch (e) {
            MKLog.warn(`Invalid URL passed to checkUrlHasAdsParam : ${url} , Error : `, e);
            return false;
        }
    }
    
}

/**
 * AdQuartile start offset interface.
 * @internal
 */
interface AdQuartileOffset {
    /**
     * AdQuartile type.
     */
    quartile: MKAdQuartile;

    /**
     * Quartile event start offset.
     */
    startOffset: number;
}

/**
 * AdTracking information.
 * @internal
 */
interface AdTracking {
    /**
     * Time offset when the tracking URL needs to be reported.
     */
    time: number;

    /**
     * The AD tracking URL.
     */
    urls: string[];
}